// Action Item Service - Single Responsibility: Manage action items only
export interface ActionItem {
  id: string
  title: string
  description: string
  status: 'pending' | 'in_progress' | 'completed' | 'cancelled' | 'on_hold'
  progress: number
  priority: 'low' | 'medium' | 'high' | 'urgent'
  estimated_duration?: number
  parent_task_id?: string
  parent_habit_id?: string
  parent_skill_id?: string
  parent_goal_id?: string
  due_date?: string
  completed_at?: string
  location?: string | null
  place_id?: number | null
  created_at: string
  updated_at: string
}

export interface CreateActionItemRequest {
  title: string
  description: string
  priority: 'low' | 'medium' | 'high'
  estimated_duration?: number
  parent_task_id?: string
  parent_habit_id?: string
  parent_skill_id?: string
  parent_goal_id?: string
  due_date?: string
  location?: string | null
  place_id?: number | null
}

export interface UpdateActionItemRequest {
  title?: string
  description?: string
  status?: 'pending' | 'in_progress' | 'completed' | 'cancelled' | 'on_hold'
  progress?: number
  priority?: 'low' | 'medium' | 'high' | 'urgent'
  estimated_duration?: number
  parent_task_id?: string | null
  parent_habit_id?: string | null
  parent_skill_id?: string | null
  parent_goal_id?: string | null
  due_date?: string
  completed_at?: string
  location?: string | null
  place_id?: number | null
}

export class ActionItemService {
  private baseUrl = '/api/hierarchy/action-items'

  async getActionItems(): Promise<ActionItem[]> {
    const response = await fetch(this.baseUrl, {
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' }
    })
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to fetch action items' }))
      throw new Error(error.error || 'Failed to fetch action items')
    }
    const data = await response.json()
    return Array.isArray(data) ? data : data.data || []
  }

  async getActionItem(id: string): Promise<ActionItem> {
    // Use the generic item endpoint: /api/hierarchy/item/<item_type>/<item_id>
    const response = await fetch(`/api/hierarchy/item/action_item/${id}`, {
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' }
    })
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: `Failed to fetch action item with id ${id}` }))
      throw new Error(error.error || `Failed to fetch action item with id ${id}`)
    }
    const result = await response.json()
    // The backend returns { data: { item: {...}, parent_chain: [...], children: [...] } }
    const item = result.data?.item || result.item || result
    // Map the backend response to our ActionItem interface
    // If we need full details, we might need to fetch from the list and find the item
    // For now, return what we have and fetch full details if needed
    if (!item.title) {
      // Fallback: fetch from list and find the item
      const allItems = await this.getActionItems()
      const fullItem = allItems.find(i => String(i.id) === String(id))
      if (fullItem) return fullItem
    }
    // Map the basic item structure to ActionItem
    return {
      id: String(item.id),
      title: item.title || '',
      description: item.description || '',
      // CON-8: default to 'pending' — 'active' is not a member of the declared
      // ActionItem status union and would violate the type / downstream checks.
      status: item.status || 'pending',
      progress: item.progress || 0,
      priority: item.priority || 'medium',
      estimated_duration: item.estimated_duration,
      parent_task_id: item.parent_task_id ? String(item.parent_task_id) : undefined,
      parent_habit_id: item.parent_habit_id ? String(item.parent_habit_id) : undefined,
      parent_skill_id: item.parent_skill_id ? String(item.parent_skill_id) : undefined,
      parent_goal_id: item.parent_goal_id ? String(item.parent_goal_id) : undefined,
      due_date: item.due_date,
      completed_at: item.completed_at,
      // Same fix as taskService.getTask: preserve location-reminder fields so
      // the edit form can render them. Otherwise every open-edit-save cycle
      // looks like it's silently blanking the place.
      place_id: item.place_id ?? null,
      location: item.location ?? null,
      created_at: item.created_at || new Date().toISOString(),
      updated_at: item.updated_at || item.created_at || new Date().toISOString()
    }
  }

  async createActionItem(actionItem: CreateActionItemRequest): Promise<ActionItem> {
    const response = await fetch(this.baseUrl, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(actionItem)
    })
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to create action item' }))
      throw new Error(error.error || 'Failed to create action item')
    }
    const data = await response.json()
    return data.data || data
  }

  async updateActionItem(id: string, updates: UpdateActionItemRequest): Promise<ActionItem> {
    const response = await fetch(`${this.baseUrl}/${id}`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates)
    })
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to update action item' }))
      throw new Error(error.error || 'Failed to update action item')
    }
    const data = await response.json()
    return data.data || data
  }

  async deleteActionItem(id: string): Promise<void> {
    const response = await fetch(`${this.baseUrl}/${id}`, {
      method: 'DELETE',
      credentials: 'include'
    })
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to delete action item' }))
      throw new Error(error.error || 'Failed to delete action item')
    }
    void import('./geofenceSyncService').then(m => m.geofenceSyncService.reconcile())
  }

  // CON-2: the backend exposes only PUT /api/hierarchy/action-items/<id>; the
  // former PATCH .../progress and .../complete sub-routes do not exist and 404.
  // Route both through the real update endpoint (updateActionItem).
  async updateActionItemProgress(id: string, progress: number): Promise<ActionItem> {
    return this.updateActionItem(id, { progress })
  }

  async markActionItemComplete(id: string): Promise<ActionItem> {
    const updated = await this.updateActionItem(id, { status: 'completed', progress: 100 })
    void import('./geofenceSyncService').then(m => m.geofenceSyncService.reconcile())
    return updated
  }
}
